// frontend/packages/business-console/src/pages/CrmCustomer.jsx
//
// CRM customer detail. Mobile layout stacks contact card → stage selector →
// note composer → activity timeline. Desktop spreads them across a two-
// column grid with the timeline taking the wider column.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';

import { BusinessContext } from '../contexts/BusinessContext';
import {
  createActivity,
  getCustomer,
  getLinkSuggestions,
  linkPerson,
  unlinkPerson,
  updateCustomer,
} from '../services/crmService';
import {
  listWorkflowFormSubmissions,
  listWorkflowForms,
} from '../services/workflowFormService';
import { listQuotes } from '../services/quoteService';
import { listInvoices } from '../services/invoiceService';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import useMobileDetect from '../hooks/useMobileDetect';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { getCachedCustomer, putCachedCustomer } from '../lib/customerCache';

const STAGES = [
  { value: 'lead', label: 'Lead' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'customer', label: 'Customer' },
  { value: 'repeat', label: 'Repeat' },
  { value: 'lost', label: 'Lost' },
];

const ACTIVITY_LABELS = {
  stage_changed: (a) =>
    `Stage changed${
      a.meta?.from || a.meta?.to
        ? ` (${a.meta?.from || 'new'} → ${a.meta?.to || ''})`
        : ''
    }`,
  note_added: () => 'Note',
  inbox_promoted: () => 'Promoted from inbox',
  form_promoted: (a) => a.body || 'Added from website form',
  email_sent: () => 'Email sent',
  manual_log: () => 'Logged activity',
  linked_person: () => 'Linked across businesses',
  unlinked_person: () => 'Unlinked across businesses',
  customer_created: () => 'Customer created',
};

const formatActivityWhen = (input) => {
  if (!input) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

const fmtMoney = (cents, currency) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'USD').toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);

// State → MUI chip color, shared by quote + invoice rows.
const DOC_STATE_COLOR = {
  draft: 'default',
  sent: 'info',
  viewed: 'info',
  accepted: 'success',
  paid: 'success',
  partial: 'warning',
  overdue: 'error',
  declined: 'error',
  void: 'default',
  canceled: 'default',
};

export default function CrmCustomer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useMobileDetect();
  const { business } = useContext(BusinessContext);

  const crmPath = useMemo(
    () => (business?.slug ? `/business/${business.slug}/crm` : '/'),
    [business?.slug]
  );
  const basePath = useMemo(
    () => (business?.slug ? `/business/${business.slug}` : ''),
    [business?.slug]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');
  const [savingStage, setSavingStage] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [postingNote, setPostingNote] = useState(false);
  // 5.9 — when online, the customer payload comes from the API +
  // gets cached. When offline, the page falls back to the cache.
  // `fromCache` drives the small "Cached" banner so the operator
  // sees the data may be stale and edits are disabled.
  const [fromCache, setFromCache] = useState(false);
  const [cachedAt, setCachedAt] = useState(null);
  const online = useOnlineStatus();

  const [linkOpen, setLinkOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const refresh = useCallback(async () => {
    setError('');
    // Offline path: skip the API call entirely, read the cache.
    if (online === false) {
      const cached = await getCachedCustomer(id);
      if (cached?.snapshot) {
        setData(cached.snapshot);
        setFromCache(true);
        setCachedAt(cached.cachedAt);
        setLoading(false);
        return;
      }
      setError(
        "You're offline and we don't have this customer cached. Reconnect to load."
      );
      setLoading(false);
      return;
    }
    try {
      const result = await getCustomer(id);
      setData(result);
      setFromCache(false);
      setCachedAt(null);
      // Fire-and-forget cache write. The submissions list is
      // fetched separately by the CustomerFormsPanel; pre-warm
      // here too so the panel works offline.
      try {
        const subs = await listWorkflowFormSubmissions({
          customerId: id,
          limit: 5,
        });
        await putCachedCustomer(id, result, subs?.items || []);
      } catch {
        await putCachedCustomer(id, result, []);
      }
    } catch (err) {
      // Network blip or 5xx — fall back to cache if we have one,
      // same UX as the offline path.
      const cached = await getCachedCustomer(id);
      if (cached?.snapshot) {
        setData(cached.snapshot);
        setFromCache(true);
        setCachedAt(cached.cachedAt);
      } else {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to load customer'
        );
      }
    } finally {
      setLoading(false);
    }
  }, [id, online]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  const customer = data?.customer;
  const activities = data?.activities || [];
  const linkedCustomers = data?.linkedCustomers || [];
  const person = data?.person;

  const handleStageChange = async (next) => {
    if (!customer) return;
    setSavingStage(true);
    try {
      await updateCustomer(customer._id, { stage: next });
      setSnack(`Stage updated to ${next}`);
      await refresh();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || 'Failed to update stage'
      );
    } finally {
      setSavingStage(false);
    }
  };

  const handleAddNote = async () => {
    const body = noteText.trim();
    if (!body || !customer) return;
    setPostingNote(true);
    try {
      await createActivity(customer._id, { kind: 'note_added', body });
      setNoteText('');
      await refresh();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || 'Failed to add note'
      );
    } finally {
      setPostingNote(false);
    }
  };

  const openLinkDialog = useCallback(async () => {
    setLinkOpen(true);
    setSuggestionsLoading(true);
    try {
      const items = await getLinkSuggestions(customer._id);
      setSuggestions(items || []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load suggestions'
      );
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [customer]);

  const handleLink = async (peer) => {
    try {
      await linkPerson(customer._id, { customerIds: [peer._id] });
      setLinkOpen(false);
      setSnack('Linked across businesses');
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Link failed');
    }
  };

  const handleUnlink = async () => {
    try {
      await unlinkPerson(customer._id);
      setSnack('Unlinked');
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unlink failed');
    }
  };

  if (loading && !data) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !customer) {
    return (
      <Box sx={{ py: 4 }}>
        <Button
          onClick={() => navigate(crmPath)}
          startIcon={<ArrowBackIosNewOutlinedIcon fontSize="small" />}
          sx={{ textTransform: 'none', mb: 2 }}
        >
          CRM
        </Button>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!customer) return null;

  const fullName =
    `${customer.firstName || ''} ${customer.lastName || ''}`.trim();

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1,
          mb: 2,
          position: { xs: 'sticky', md: 'static' },
          top: { xs: 56, md: 'auto' },
          zIndex: 1,
          backgroundColor: 'background.default',
        }}
      >
        <IconButton
          onClick={() => navigate(crmPath)}
          aria-label="Back to CRM"
          edge="start"
        >
          <ArrowBackIosNewOutlinedIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, flex: 1 }}
          noWrap
        >
          {fullName || customer.email || 'Customer'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            component={RouterLink}
            to={`${basePath}/quotes/new?customerId=${customer._id}`}
            size="small"
            variant="outlined"
            startIcon={<RequestQuoteOutlinedIcon fontSize="small" />}
            sx={{ textTransform: 'none' }}
            disabled={fromCache}
          >
            New estimate
          </Button>
          <Button
            component={RouterLink}
            to={`${basePath}/invoices/new?customerId=${customer._id}`}
            size="small"
            variant="contained"
            startIcon={<ReceiptLongOutlinedIcon fontSize="small" />}
            sx={{ textTransform: 'none' }}
            disabled={fromCache}
          >
            New invoice
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {fromCache && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {online
            ? "Couldn't reach the server — showing cached data."
            : "You're offline. Showing cached data; edits are disabled until you reconnect."}
          {cachedAt ? ` Cached ${new Date(cachedAt).toLocaleString()}.` : ''}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 360px) 1fr' },
          gap: 2,
        }}
      >
        {/* Left column: contact + stage + linked customers */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ width: 56, height: 56, fontSize: 22 }}>
                {(
                  customer.firstName?.[0] ||
                  customer.email?.[0] ||
                  '?'
                ).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                  {fullName || 'Unnamed'}
                </Typography>
                {customer.tags?.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      mt: 0.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    {customer.tags.map((t) => (
                      <Chip
                        key={t}
                        size="small"
                        label={t}
                        sx={{ height: 20 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {customer.email && (
                <Box
                  component="a"
                  href={`mailto:${customer.email}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  <EmailOutlinedIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary' }}
                  />
                  <Typography variant="body2">{customer.email}</Typography>
                </Box>
              )}
              {customer.phone && (
                <Box
                  component="a"
                  href={`tel:${customer.phone}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  <PhoneOutlinedIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary' }}
                  />
                  <Typography variant="body2">{customer.phone}</Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
            >
              Stage
            </Typography>
            <Select
              value={customer.stage || ''}
              onChange={(e) => handleStageChange(e.target.value)}
              size="small"
              fullWidth
              disabled={savingStage || fromCache}
            >
              {STAGES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Cross-business links
              </Typography>
              {customer.personId ? (
                <Tooltip title="Unlink">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleUnlink}
                      disabled={fromCache}
                    >
                      <LinkOffOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  size="small"
                  startIcon={<LinkOutlinedIcon fontSize="small" />}
                  onClick={openLinkDialog}
                  sx={{ textTransform: 'none' }}
                  disabled={fromCache}
                >
                  Link
                </Button>
              )}
            </Box>

            {linkedCustomers.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {customer.personId
                  ? "Linked, but the matching customers are in businesses you can't see."
                  : 'Not linked to any other business.'}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {linkedCustomers.map((peer) => (
                  <Box
                    key={peer._id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      p: 1.25,
                      borderRadius: 1,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {`${peer.firstName || ''} ${peer.lastName || ''}`.trim() ||
                        peer.email}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {peer.email || peer.phone || '—'}
                      {peer.stage ? ` · ${peer.stage}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {person?.displayName && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mt: 1 }}
              >
                Linked as: {person.displayName}
              </Typography>
            )}
          </Paper>
        </Box>

        {/* Right column: note composer + activity timeline */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Add a note
            </Typography>
            <TextField
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Called, left voicemail. Following up Friday."
              multiline
              minRows={2}
              fullWidth
              disabled={postingNote || fromCache}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                variant="contained"
                onClick={handleAddNote}
                disabled={postingNote || !noteText.trim() || fromCache}
                startIcon={
                  postingNote ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SendOutlinedIcon />
                  )
                }
                sx={{ minWidth: { xs: '100%', sm: 140 }, py: 1.25 }}
              >
                {postingNote ? 'Saving…' : 'Save note'}
              </Button>
            </Box>
          </Paper>

          <CustomerFormsPanel
            customerId={customer._id}
            customerName={fullName || customer.email || ''}
            basePath={basePath}
            fromCache={fromCache}
          />

          <CustomerDocsPanel
            customerId={customer._id}
            basePath={basePath}
            fromCache={fromCache}
          />

          <Paper
            variant="outlined"
            sx={{ p: { xs: 1.5, md: 2.5 }, borderRadius: 2 }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 1, px: { xs: 1, md: 0 } }}
            >
              Activity
            </Typography>
            {activities.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', px: { xs: 1, md: 0 } }}
              >
                No activity yet.
              </Typography>
            ) : (
              <Box sx={{ position: 'relative', pl: 3 }}>
                {/* Vertical timeline rail */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 9,
                    top: 4,
                    bottom: 4,
                    width: 2,
                    backgroundColor: 'divider',
                  }}
                />
                {activities.map((a) => {
                  const labelFn = ACTIVITY_LABELS[a.kind] || (() => a.kind);
                  return (
                    <Box key={a._id} sx={{ position: 'relative', pb: 2 }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -22,
                          top: 6,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          border: 2,
                          borderColor: 'background.paper',
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block' }}
                      >
                        {labelFn(a)} · {formatActivityWhen(a.createdAt)}
                      </Typography>
                      {a.body && (
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}
                        >
                          {a.body}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: { xs: 0, md: 2 } } }}
      >
        <DialogTitle>Link across businesses</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            We searched your other businesses for customers with this email.
            Linking keeps records separate per business but lets you see this
            person across all of them.
          </Typography>
          {suggestionsLoading ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : suggestions.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No matching customers in your other businesses.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {suggestions.map((s) => (
                <Paper
                  key={s._id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {`${s.firstName || ''} ${s.lastName || ''}`.trim() ||
                        s.email}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', display: 'block' }}
                    >
                      {s.email || s.phone || '—'}
                      {s.stage ? ` · ${s.stage}` : ''}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleLink(s)}
                    sx={{ textTransform: 'none' }}
                  >
                    Link
                  </Button>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLinkOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={2500}
        onClose={() => setSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snack}
      />
    </Box>
  );
}

// ── 4.11b — Customer profile Workflow Forms section ────────────────
//
// Lists the most recent workflow-form submissions for this customer.
// Each row deep-links to the submission detail page
// (4.11b /forms/submissions/:id). Soft-fails when the customer has
// no submissions yet (returns null so the customer profile stays
// clean for fresh leads).

function CustomerFormsPanel({ customerId, customerName, basePath, fromCache }) {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // "Fill new form" launcher — lazily loads the list of published
  // forms when the menu opens, then deep-links to the renderer with
  // the customer pre-attached so the submission lands on this record.
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [forms, setForms] = React.useState(null);
  const [formsLoading, setFormsLoading] = React.useState(false);

  const openFillMenu = async (e) => {
    setMenuAnchor(e.currentTarget);
    if (forms !== null) return; // already loaded this session
    setFormsLoading(true);
    try {
      const res = await listWorkflowForms({ limit: 200 });
      setForms(res.items || []);
    } catch {
      setForms([]);
    } finally {
      setFormsLoading(false);
    }
  };

  const startForm = (slug) => {
    setMenuAnchor(null);
    const qs = new URLSearchParams({ customerId });
    if (customerName) qs.set('customerName', customerName);
    navigate(
      `${basePath}/forms/${encodeURIComponent(slug)}/new?${qs.toString()}`
    );
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      // 5.9 — when the parent CrmCustomer is rendering from the
      // IndexedDB cache, skip the live submissions call entirely
      // and use the snapshot bundled with the customer. Same path
      // for offline + transient API failure.
      if (fromCache) {
        try {
          const cached = await getCachedCustomer(customerId);
          if (!cancelled) {
            setSubmissions(cached?.submissions || []);
            setLoading(false);
          }
        } catch {
          if (!cancelled) {
            setSubmissions([]);
            setLoading(false);
          }
        }
        return;
      }
      try {
        const res = await listWorkflowFormSubmissions({
          customerId,
          limit: 5,
        });
        if (!cancelled) setSubmissions(res.items || []);
      } catch {
        // Live fetch failed — try the cache as a backstop so the
        // panel still renders something useful on a flaky network.
        try {
          const cached = await getCachedCustomer(customerId);
          if (!cancelled) setSubmissions(cached?.submissions || []);
        } catch {
          if (!cancelled) setSubmissions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, fromCache]);

  if (loading) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Workflow forms
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Tooltip
            title={
              fromCache
                ? 'Reconnect to start a new form'
                : 'Fill a form for this customer'
            }
          >
            <span>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<PostAddOutlinedIcon fontSize="small" />}
                onClick={openFillMenu}
                disabled={fromCache}
                sx={{ textTransform: 'none' }}
              >
                Fill new form
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            startIcon={<DescriptionOutlinedIcon fontSize="small" />}
            component={RouterLink}
            to={`${basePath}/forms`}
            sx={{ textTransform: 'none' }}
          >
            All forms
          </Button>
        </Stack>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {formsLoading ? (
          <MenuItem disabled>Loading forms…</MenuItem>
        ) : forms && forms.length > 0 ? (
          forms.map((f) => (
            <MenuItem key={f._id} onClick={() => startForm(f.slug)}>
              {f.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No published forms yet</MenuItem>
        )}
      </Menu>

      {submissions.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No forms filled for this customer yet.
        </Typography>
      ) : null}

      <Stack divider={<Divider />} spacing={0}>
        {submissions.map((s) => {
          const formattedAt = formatActivityWhen(s.submittedAt);
          return (
            <Box
              key={s._id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`${basePath}/forms/submissions/${s._id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`${basePath}/forms/submissions/${s._id}`);
                }
              }}
              sx={{
                py: 1,
                px: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.formName || s.formSlug || 'Workflow form'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  v{s.formVersion} · {formattedAt}
                  {s.attachmentCount > 0
                    ? ` · ${s.attachmentCount} attachment${
                        s.attachmentCount === 1 ? '' : 's'
                      }`
                    : ''}
                </Typography>
              </Box>
              {s.attachmentCount > 0 ? (
                <AttachFileOutlinedIcon
                  fontSize="small"
                  sx={{ color: 'text.secondary', flexShrink: 0 }}
                />
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

// 4.17 — Estimates + Invoices for this customer, alongside the forms
// panel, so the customer record shows every document in one place.
// Online-only (financial docs aren't part of the 5.9 offline cache);
// renders nothing when empty so a brand-new customer's profile stays
// clean. Rows deep-link to the existing quote/invoice detail pages,
// which own their own view / send / download actions.
function DocRow({ to, navigate, primary, secondary, trailing }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => navigate(to)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(to);
        }
      }}
      sx={{
        py: 1,
        px: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        borderRadius: 1,
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {primary}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {secondary}
        </Typography>
      </Box>
      {trailing}
    </Box>
  );
}

function CustomerDocsPanel({ customerId, basePath, fromCache }) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = React.useState([]);
  const [invoices, setInvoices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (fromCache) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const [q, inv] = await Promise.all([
        listQuotes({ customerId, limit: 5 }).catch(() => ({ items: [] })),
        listInvoices({ customerId, limit: 5 }).catch(() => ({ items: [] })),
      ]);
      if (!cancelled) {
        setQuotes(q.items || []);
        setInvoices(inv.items || []);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, fromCache]);

  if (loading || fromCache) return null;
  if (quotes.length === 0 && invoices.length === 0) return null;

  const stateChip = (state) => (
    <Chip
      size="small"
      variant="outlined"
      color={DOC_STATE_COLOR[state] || 'default'}
      label={state || '—'}
      sx={{
        height: 22,
        fontSize: 11,
        textTransform: 'capitalize',
        flexShrink: 0,
      }}
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      {quotes.length > 0 ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Estimates
            </Typography>
            <Button
              size="small"
              startIcon={<RequestQuoteOutlinedIcon fontSize="small" />}
              component={RouterLink}
              to={`${basePath}/quotes`}
              sx={{ textTransform: 'none' }}
            >
              All
            </Button>
          </Box>
          <Stack
            divider={<Divider />}
            spacing={0}
            sx={{ mb: invoices.length > 0 ? 2 : 0 }}
          >
            {quotes.map((q) => (
              <DocRow
                key={q._id}
                navigate={navigate}
                to={`${basePath}/quotes/${q._id}`}
                primary={`${q.number || 'Estimate'}${
                  q.title ? ` · ${q.title}` : ''
                }`}
                secondary={formatActivityWhen(q.createdAt)}
                trailing={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {fmtMoney(q.total, q.currency)}
                    </Typography>
                    {stateChip(q.state)}
                  </Stack>
                }
              />
            ))}
          </Stack>
        </>
      ) : null}

      {invoices.length > 0 ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Invoices
            </Typography>
            <Button
              size="small"
              startIcon={<ReceiptLongOutlinedIcon fontSize="small" />}
              component={RouterLink}
              to={`${basePath}/invoices`}
              sx={{ textTransform: 'none' }}
            >
              All
            </Button>
          </Box>
          <Stack divider={<Divider />} spacing={0}>
            {invoices.map((inv) => (
              <DocRow
                key={inv._id}
                navigate={navigate}
                to={`${basePath}/invoices/${inv._id}`}
                primary={`${inv.number || 'Invoice'}${
                  inv.title ? ` · ${inv.title}` : ''
                }`}
                secondary={formatActivityWhen(inv.createdAt)}
                trailing={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {fmtMoney(
                        inv.amountDue != null ? inv.amountDue : inv.total,
                        inv.currency
                      )}
                    </Typography>
                    {stateChip(inv.state)}
                  </Stack>
                }
              />
            ))}
          </Stack>
        </>
      ) : null}
    </Paper>
  );
}
